/**
 * 公開見積画面 (/quote/:token)。認証不要、印刷 CSS で A4 最適化。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §8
 *
 * - issuedAt / validUntil 表示は firestoreTime.formatDate を使用（Callable シリアライズ対応）。
 * - categoryLogo がある場合、指定位置（top-left/center/right）にロゴを表示。
 */
import type { PublicQuoteView } from '@goenhub/shared';
import { Printer } from 'lucide-react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { CategoryLogoHeader } from '@/components/common/CategoryLogoHeader';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { usePublicPageMode } from '@/hooks/usePublicPageMode';
import { usePublicQuote } from '@/hooks/useQuotes';
import { formatDate, formatYmd } from '@/lib/firestoreTime';

const fmtDate = (v: unknown) => {
  const f = formatDate(v);
  return f === '—' ? '-' : f;
};

/** T2: 納品先が請求先と同じ場合は表示を省略するための判定。 */
function isSameAsBilling(
  billing: PublicQuoteView['customer'],
  delivery: NonNullable<PublicQuoteView['delivery']>,
): boolean {
  return (
    (billing.postalCode ?? '') === (delivery.postalCode ?? '') &&
    (billing.prefecture ?? '') === (delivery.prefecture ?? '') &&
    (billing.address ?? '') === (delivery.address ?? '') &&
    (billing.buildingName ?? '') === (delivery.buildingName ?? '')
  );
}

export function PublicQuotePage() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading } = usePublicQuote(token ?? null);

  // globals.css の body { overflow:hidden; height:100vh } は AppLayout 専用。
  // AppLayout 外のこのページでは解除しないと縦スクロールできない。
  usePublicPageMode();

  // PDF/印刷時のファイル名: 【見積書】お客様名様_YYYYMMDD
  useEffect(() => {
    const q = data?.status === 'ok' ? data.quote : null;
    if (!q) return;
    const prev = document.title;
    const name = q.customer?.name?.trim();
    const ymd = formatYmd(q.issuedAt);
    document.title = ['【見積書】', name ? `${name}様` : '', ymd ? `_${ymd}` : ''].join('').trim();
    return () => {
      document.title = prev;
    };
  }, [data]);

  if (isLoading) return <Spinner />;
  if (!data) return <p>見積を読み込めません</p>;
  if (data.status === 'not_found')
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold mb-2">見積書が見つかりません</h1>
        <p>URL をご確認ください。</p>
      </div>
    );
  if (data.status === 'expired')
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold mb-2">見積書の有効期限が切れています</h1>
        <p>お手数ですが、再発行をご依頼ください。</p>
      </div>
    );

  const quote: PublicQuoteView = data.quote;
  // 行割引の合計 (税抜)。> 0 のときだけ合計欄に割引前小計/割引合計の行を出す
  const discountTotal = quote.lineItems.reduce(
    (a, it) => a + (it.discountAmountExcludingTax ?? 0),
    0,
  );
  // 割引金額の非表示設定 (見積作成時のオプション)。
  // true のとき: 行割引の内訳・割引前小計/割引合計を出さず、単価は割引後の実質単価で表示する。
  const hideDiscounts = quote.hideDiscounts === true;
  // 型番の非表示設定 (見積作成時のオプション)。true のとき品目名の下の型番を出さない。
  const hideModelNames = quote.hideModelNames === true;

  return (
    <>
      <style>{`
        @page { size: A4; margin: 15mm; }
        @media print {
          /* 100vh 固定・overflow:hidden のアプリ用レイアウトを印刷時は解除。
             残しておくと内容が切れたり空白ページが生まれる。 */
          html, body, #root {
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          body { background: white; }
          .no-print { display: none !important; }
          .quote-page { box-shadow: none !important; padding: 0 !important; }
        }
      `}</style>
      <div className="min-h-screen print:min-h-0 bg-[var(--bg-root)] py-8 print:py-0">
        <div className="no-print max-w-[210mm] mx-auto mb-4 flex justify-end">
          <Button type="button" variant="primary" onClick={() => window.print()}>
            <Printer />
            PDF/印刷
          </Button>
        </div>

        {/* 画面では A4 相当の紙面に見せるため min-h を持たせるが、印刷時は解除する。
            @page margin 15mm を引いた実印刷領域は 267mm しかないため、
            297mm を残すと必ず余白だけの 2 ページ目が生成される。 */}
        <div className="quote-page max-w-[210mm] mx-auto min-h-[297mm] print:min-h-0 bg-white text-black p-12 shadow-md print:shadow-none">
          <CategoryLogoHeader logo={quote.categoryLogo ?? null} />
          <header className="border-b-2 border-black pb-4 mb-6">
            <h1 className="text-2xl font-bold tracking-wide text-center">御 見 積 書</h1>
          </header>

          <div className="grid grid-cols-2 gap-8 mb-6">
            <div>
              <p className="text-sm">
                {quote.customer.companyName ? (
                  <>
                    <span className="font-semibold">{quote.customer.companyName}</span>
                    <br />
                  </>
                ) : null}
                {quote.customer.department && (
                  <>
                    {quote.customer.department}
                    <br />
                  </>
                )}
                <span className="text-base font-medium">{quote.customer.name} 様</span>
              </p>
              {quote.customer.postalCode && (
                <p className="text-xs mt-2">
                  〒{quote.customer.postalCode}
                  <br />
                  {quote.customer.prefecture}
                  {quote.customer.address}
                  {quote.customer.buildingName ?? ''}
                </p>
              )}
              {/* T2: 納品先 (請求先と異なる場合のみ表示)
                  見積書では「予定」を付与し、契約前であることを示す。
                  BtoB → 「ご納品先（予定）」、BtoC → 「お届け先（予定）」 */}
              {quote.delivery && !isSameAsBilling(quote.customer, quote.delivery) && (
                <div className="mt-3 pt-2 border-t border-gray-300 text-xs">
                  <p className="font-semibold mb-0.5">
                    {quote.contactKind === 'btob' ? 'ご納品先（予定）' : 'お届け先（予定）'}
                    {quote.delivery.label ? ` (${quote.delivery.label})` : ''}
                  </p>
                  {quote.delivery.recipientName && <p>宛: {quote.delivery.recipientName}</p>}
                  {quote.delivery.postalCode && (
                    <p>
                      〒{quote.delivery.postalCode} {quote.delivery.prefecture ?? ''}
                      {quote.delivery.address ?? ''}
                      {quote.delivery.buildingName ?? ''}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="text-right text-sm">
              <p>発行日: {fmtDate(quote.issuedAt)}</p>
              <p>有効期限: {fmtDate(quote.validUntil)}</p>
              <p>No. {quote.quoteId.slice(0, 12)}</p>
            </div>
          </div>

          <div className="mb-6 text-right">
            <p className="text-xs">{quote.vendor.companyName}</p>
            {quote.vendor.postalCode && <p className="text-xs">〒{quote.vendor.postalCode}</p>}
            {quote.vendor.address && <p className="text-xs">{quote.vendor.address}</p>}
            {quote.vendor.phone && <p className="text-xs">TEL: {quote.vendor.phone}</p>}
            {quote.vendor.email && <p className="text-xs">{quote.vendor.email}</p>}
            {quote.vendor.representativeName && (
              <p className="text-xs mt-1">{quote.vendor.representativeName}</p>
            )}
          </div>

          <p className="text-base font-medium mb-2">
            件名: <span className="font-normal">{quote.title}</span>
          </p>
          <p className="text-sm mb-4">下記の通り、お見積もり申し上げます。</p>

          <div className="bg-gray-100 p-3 mb-4 inline-block">
            <span className="text-sm">合計金額 (税込)</span>
            <span className="ml-3 text-xl font-bold tabular-nums">
              ¥{quote.totalIncludingTax.toLocaleString()}
            </span>
          </div>

          <table className="w-full text-sm border-collapse mb-6">
            <thead>
              <tr className="border-y-2 border-black">
                <th className="text-left p-2">品目</th>
                <th className="text-right p-2 w-20">数量</th>
                <th className="text-right p-2 w-28">単価 (税抜)</th>
                <th className="text-right p-2 w-32">金額 (税込)</th>
              </tr>
            </thead>
            <tbody>
              {quote.lineItems.map((it, i) => (
                <tr key={i} className="border-b border-gray-300">
                  <td className="p-2">
                    {it.name}
                    {it.variation && (
                      <span className="ml-1 text-xs text-gray-500">({it.variation})</span>
                    )}
                    {!hideModelNames && it.modelName && (
                      <div className="text-xs text-gray-500">{it.modelName}</div>
                    )}
                    {!hideDiscounts && (it.discountAmountExcludingTax ?? 0) > 0 && (
                      <div className="text-xs text-gray-700">
                        割引
                        {it.discountType === 'percent' && it.discountValue != null
                          ? ` (${it.discountValue}%)`
                          : ''}
                        : −¥
                        {(it.discountAmountExcludingTax ?? 0).toLocaleString()}
                        （税抜）
                      </div>
                    )}
                  </td>
                  <td className="text-right p-2 tabular-nums">{it.quantity}</td>
                  <td className="text-right p-2 tabular-nums">
                    ¥
                    {(hideDiscounts && it.quantity > 0
                      ? Math.round(it.subtotalExcludingTax / it.quantity)
                      : it.priceExcludingTax
                    ).toLocaleString()}
                  </td>
                  <td className="text-right p-2 tabular-nums">
                    ¥{it.subtotalIncludingTax.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {!hideDiscounts && discountTotal > 0 && (
                <>
                  <tr className="border-t border-black">
                    <td colSpan={3} className="p-2 text-right">
                      割引前小計 (税抜)
                    </td>
                    <td className="text-right p-2 tabular-nums">
                      ¥{(quote.subtotalExcludingTax + discountTotal).toLocaleString()}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300">
                    <td colSpan={3} className="p-2 text-right">
                      割引合計 (税抜)
                    </td>
                    <td className="text-right p-2 tabular-nums">
                      −¥{discountTotal.toLocaleString()}
                    </td>
                  </tr>
                </>
              )}
              <tr className="border-y border-black">
                <td colSpan={3} className="p-2 text-right">
                  小計 (税抜)
                </td>
                <td className="text-right p-2 tabular-nums">
                  ¥{quote.subtotalExcludingTax.toLocaleString()}
                </td>
              </tr>
              <tr className="border-b border-black">
                <td colSpan={3} className="p-2 text-right">
                  消費税
                </td>
                <td className="text-right p-2 tabular-nums">¥{quote.taxAmount.toLocaleString()}</td>
              </tr>
              {quote.hasTradeIn &&
                quote.tradeInType === 'discount' &&
                (quote.tradeInAmount ?? 0) > 0 && (
                  <tr className="border-b border-black">
                    <td colSpan={3} className="p-2 text-right">
                      下取り値引
                    </td>
                    <td className="text-right p-2 tabular-nums">
                      −¥{(quote.tradeInAmount ?? 0).toLocaleString()}
                    </td>
                  </tr>
                )}
              <tr className="border-b-2 border-black bg-gray-50">
                <td colSpan={3} className="p-2 text-right font-bold">
                  合計 (税込)
                </td>
                <td className="text-right p-2 tabular-nums font-bold">
                  ¥{quote.totalIncludingTax.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>

          <div className="text-sm space-y-1 mb-6">
            <p>
              <span className="font-semibold">支払方法:</span>{' '}
              {quote.paymentMethod === 'transfer' ? '銀行振込' : 'クレジットカード'}
              {quote.installments ? ` (${quote.installments}回払い)` : ''}
            </p>
            {quote.hasTradeIn && (
              <p>
                <span className="font-semibold">下取り:</span> あり (
                {quote.tradeInType === 'discount' ? '割引' : 'キャッシュバック'})
                {quote.tradeInAmount && ` ¥${quote.tradeInAmount.toLocaleString()}`}
              </p>
            )}
          </div>

          {quote.remarks && (
            <div className="text-xs text-gray-700 border-t pt-3 mb-4">
              <p className="font-semibold mb-1">備考</p>
              <p className="whitespace-pre-wrap">{quote.remarks}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
